/**
 * Appendices Tab Component
 * 
 * Shows required appendices and supporting documents.
 */

import React from 'react';
import { FileText, CheckSquare, Download } from 'lucide-react';

interface AppendicesTabProps {
  appendices: string[];
}

export function AppendicesTab({ appendices }: AppendicesTabProps) {
  const [checkedItems, setCheckedItems] = React.useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedItems(newChecked);
  };

  const exportChecklist = () => {
    const lines = appendices.map((item, idx) => 
      `${checkedItems.has(idx) ? '☑' : '☐'} ${item}`
    );
    const text = `Appendices Checklist\n\n${lines.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'appendices-checklist.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const completionPercent = appendices.length > 0
    ? Math.round((checkedItems.size / appendices.length) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
            Required Appendices
          </h3>
          <p className="text-sm text-gray-600 dark:text-theme-text-secondary mt-1">
            {checkedItems.size} of {appendices.length} completed ({completionPercent}%)
          </p>
        </div>
        <button
          onClick={exportChecklist}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Download className="h-4 w-4" />
          Export Checklist
        </button>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-green-600 dark:bg-green-500 transition-all duration-300"
          style={{ width: `${completionPercent}%` }}
        />
      </div>

      {/* Appendices List */}
      {appendices.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-theme-text-secondary">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No specific appendices identified in the RFP.</p>
          <p className="text-sm mt-2">Consider including standard supporting documents.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {appendices.map((item, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
                checkedItems.has(index)
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <button
                onClick={() => toggleItem(index)}
                className="flex-shrink-0 mt-0.5"
              >
                {checkedItems.has(index) ? (
                  <CheckSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <div className="h-5 w-5 border-2 border-gray-300 dark:border-gray-600 rounded" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm ${
                    checkedItems.has(index)
                      ? 'text-green-900 dark:text-green-300 line-through'
                      : 'text-gray-900 dark:text-theme-text'
                  }`}
                >
                  {item.replace(/^[-*•]\s*/, '').trim()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-900 dark:text-blue-300">
          <strong>Instructions:</strong> Check off each appendix as you complete and attach it to your proposal. Ensure all required documents are included before submission.
        </p>
      </div>
    </div>
  );
}
